import React, { Suspense, useEffect } from 'react';
import { Redirect, Switch } from 'react-router-dom';

import Layout from './Layout';
import { useAuth } from '../../hooks';
import { Page404 } from '../../pages';
import { useRoutes } from './getRoutes';
import { Loading, PermissionCheck, Route } from '../../components';
import type { MenuItem } from './Layout';
import { ORG_ID_ROUTE_TOKEN, ROUTE_PATH } from './const';
import { OrgScopedRouteGuard } from './OrgScopedRouteGuard';
import { buildAnalyticsPath, buildConversationPath } from './orgRouteUtils';
import { PERMISSIONS } from '../../shared/constants/permissions';

const ProtectedRoutes = React.lazy(() => import('./ProtectedRoutes'));

const mapItemsAsRoutes = (menuItems: MenuItem[]) => {
  return menuItems.map(route => {
    const Component = route.component;
    if (!Component) return null;

    const isOrgScopedRoute = route.url?.includes(ORG_ID_ROUTE_TOKEN);
    const element = isOrgScopedRoute ? (
      <OrgScopedRouteGuard>
        <Component />
      </OrgScopedRouteGuard>
    ) : (
      <Component />
    );

    return (
      <Route
        exact
        key={route.url}
        path={route.url}
        action={route.action}
        title={route.pageTitle || route.menuTitle}
      >
        {element}
      </Route>
    );
  });
};

const AutoLoginRedirect = () => {
  const { loginWithRedirect } = useAuth();

  useEffect(() => {
    loginWithRedirect();
  }, [loginWithRedirect]);

  return null;
};

const AutoRouter = () => {
  const { organization_id, orgs, isLoading } = useAuth();

  if (isLoading) return null;

  const resolvedOrgId = organization_id ?? orgs[0]?.id;
  if (!resolvedOrgId) return null;

  const analyticsBase = buildAnalyticsPath(resolvedOrgId);
  const conversationBase = buildConversationPath(resolvedOrgId);

  return (
    <PermissionCheck
      action={PERMISSIONS.SUPER_ADMIN}
      fallback={
        <PermissionCheck
          action={PERMISSIONS.VIEW_AGENTS}
          fallback={<Redirect to={conversationBase} />}
        >
          <Redirect to={analyticsBase} />
        </PermissionCheck>
      }
    >
      <Redirect to={analyticsBase} />
    </PermissionCheck>
  );
};

const Routes = () => {
  const { loginWithRedirect, logout, isAuthenticated, isLoading } = useAuth();
  const { publicRoutes, privateRoutes, menuItems } = useRoutes(
    loginWithRedirect,
    logout,
    isAuthenticated
  );

  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <AutoLoginRedirect />;
  }

  return (
    <Layout menuItems={menuItems}>
      <Suspense fallback={null}>
        <Switch>
          <Route path={ROUTE_PATH.NOT_FOUND} exact title="404">
            <Page404 />
          </Route>
          {mapItemsAsRoutes(publicRoutes)}
          <Route path="/">
            <ProtectedRoutes>
              {mapItemsAsRoutes(privateRoutes)}
              <Route path="/" exact>
                <AutoRouter />
              </Route>
              <Route path="*" title="404">
                <Page404 />
              </Route>
            </ProtectedRoutes>
          </Route>
        </Switch>
      </Suspense>
    </Layout>
  );
};

export default Routes;
